/**
 * `<Countdown>` — a 1-minute market **is** a clock, so the clock is a
 * first-class object rather than a label (components.md §2.1).
 *
 * **It never calls `setState`.** Plan §2.4 is a hard invariant: the countdown
 * holds its own `requestAnimationFrame` budget on the `critical` lane and is
 * never starved by book churn. A component that re-rendered at 10 Hz through
 * React would be competing with 50 book deltas a second for the same reconciler,
 * on exactly the five seconds the user is watching. So the loop below writes
 * three things directly — the digits' `textContent`, one `data-band` attribute,
 * and one `stroke-dasharray` — and React re-renders only when the *market*
 * changes.
 *
 * **It renders `expiry_at_ms − t_server_estimate`, ticking on
 * `performance.now()`** (D9). `Date.now()` is banned outside `lib/clock/` and
 * eslint enforces it. A countdown driven by the browser's wall clock is wrong by
 * however much the user's clock is wrong, and being *behind* is the dangerous
 * direction — it tells the user there is more time left than there is.
 *
 * **The server is the sole authority on state.** If the arithmetic says `0.4`
 * and a `market.{id}` frame says `EXPIRED`, the frame wins immediately: `band`
 * comes from `countdownView`, which checks the state before it checks the clock.
 *
 * The width of `88.8` is reserved at all times so the decimal appearing in the
 * final ten seconds does not reflow the digits — the one moment in the market
 * when a 3px shift is most visible and least forgivable.
 *
 * **Every `group-data-[band=…]` class below is written out as a literal, and it
 * is `group-data-`, not `data-`.** Two silent failures live in that sentence.
 * Tailwind v4 generates utilities by scanning source text, so a class assembled
 * at runtime from a lookup table produces no CSS at all; and a bare
 * `data-[band=…]:` variant matches the element's *own* attribute, so putting it
 * on the digits while the attribute lives on the root produced a countdown that
 * never changed colour — through the `Late` band, through `Final`, through the
 * last three seconds. Both failures look exactly like working code. This is the
 * Phase 3 `unicode-range` lesson in a different costume, and it was caught the
 * same way: by looking at the page in a browser.
 */

import { useEffect, useRef } from 'react'
import { cx } from '@/lib/cx'
import { formatCountdown } from '@/lib/format'
import { sharedScheduler } from '@/lib/raf'
import { countdownView, type CountdownBand } from '@/domain/countdown'
import type { MarketState } from '@/domain/lifecycle'
import type { Clock } from '@/lib/clock'

export interface CountdownProps {
  state: MarketState
  anchorAtMs: number
  openAtMs: number
  expiryAtMs: number
  clock: Clock
  /** The socket is down or the panel is unconfirmed: desaturate rather than lie.
   *  A confidently wrong clock is worse than a visibly uncertain one (§2.1). */
  stale?: boolean
  /** 2px bottom bar instead of the ring — the sub-768px treatment. */
  bar?: boolean
  /**
   * The chart's background layer, rather than a control in the strip.
   *
   * A large ring with the digits inside it, filling its container and painted
   * behind the candles. It is **the** countdown, not a copy of one: the strip
   * stopped carrying its own, so this keeps `role="timer"` and everything a
   * screen reader is given.
   *
   * That is why it is dim rather than faint. A 100px numeral and a 3px ring
   * carry at an alpha that would be illegible on body text — which is APCA's
   * own point, that the contrast a thing needs falls as it gets larger — so it
   * can recede behind the price and still be the first thing you read.
   *
   * Everything else is shared. The rAF loop, the bands, the 10 Hz final phase
   * and the pulse are all driven off the same `data-band` attribute and the
   * same three ref writes, so the variant is a different shape around one
   * clock rather than a second one.
   */
  underlay?: boolean
  className?: string
}

const RING_SIZE = 56
const RING_STROKE = 2
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS
/** Thicker than the strip's 2px, because the underlay is scaled up several
 *  times and a hairline at that size disappears into the candles. */
const UNDERLAY_STROKE = 3

/**
 * The band table, as literal Tailwind classes.
 *
 * | Band | Remaining | Colour |
 * |---|---|---|
 * | `FIXING` | pre-open, counts to `open_at_ms` | `text-tertiary` / ring `border-subtle` |
 * | `OPEN` | ≥ 30s | `text-primary` / ring `accent-solid` |
 * | `LATE` | 30–10s | `warning-text` / ring `warning-solid` |
 * | `FINAL` | < 10s | `down-text` / ring `down-solid`, 10 Hz, one decimal, 1s pulse |
 * | `LAST_CALL` | < 3s | as `FINAL`, digits step to `display`, pulse tightens to 300ms |
 * | `EXPIRED` | ≤ 0 | hands off — `<ResolutionPanel>` takes the slot in Phase 7 |
 */
const DIGIT_BANDS = [
  'group-data-[band=FIXING]:text-text-tertiary',
  'group-data-[band=OPEN]:text-text-primary',
  'group-data-[band=LATE]:text-warning-text',
  'group-data-[band=FINAL]:text-down-text',
  'group-data-[band=LAST_CALL]:text-down-text',
  'group-data-[band=EXPIRED]:text-text-tertiary',
  // The one size change in the whole component, and it belongs to the three
  // seconds in which a human can still read a book, decide and click.
  'group-data-[band=LAST_CALL]:text-display',
].join(' ')

const ARC_BANDS = [
  'group-data-[band=FIXING]:stroke-border-subtle',
  'group-data-[band=OPEN]:stroke-accent-solid',
  'group-data-[band=LATE]:stroke-warning-solid',
  'group-data-[band=FINAL]:stroke-down-solid',
  'group-data-[band=LAST_CALL]:stroke-down-solid',
  'group-data-[band=EXPIRED]:stroke-border-strong',
].join(' ')

const BAR_BANDS = [
  'group-data-[band=FIXING]:bg-border-subtle',
  'group-data-[band=OPEN]:bg-accent-solid',
  'group-data-[band=LATE]:bg-warning-solid',
  'group-data-[band=FINAL]:bg-down-solid',
  'group-data-[band=LAST_CALL]:bg-down-solid',
  'group-data-[band=EXPIRED]:bg-border-strong',
].join(' ')

export function Countdown({
  state,
  anchorAtMs,
  openAtMs,
  expiryAtMs,
  clock,
  stale,
  bar,
  underlay,
  className,
}: CountdownProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const digitsRef = useRef<HTMLSpanElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)
  const arcRef = useRef<SVGCircleElement>(null)
  const preOpenRef = useRef<SVGCircleElement>(null)
  const barRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const root = rootRef.current
    const digits = digitsRef.current
    if (!root || !digits) return

    let lastPaintMs = -Infinity
    let lastText = ''
    let lastBand: CountdownBand | null = null

    const paint = (frameMs: number, force = false) => {
      const view = countdownView({
        state,
        anchorAtMs,
        openAtMs,
        expiryAtMs,
        nowMs: clock.now(),
      })

      // 4 Hz, rising to 10 Hz once the decimal appears (D9). Painting every
      // frame would be sixty writes a second to show a number that changes four
      // times.
      if (!force && frameMs - lastPaintMs < view.tickIntervalMs) return
      lastPaintMs = frameMs

      const text = formatCountdown(view.remainingMs)
      // Compare the *rendered* string, not the milliseconds: at 4 Hz three
      // frames in four produce identical digits, and writing `textContent`
      // unchanged still dirties layout.
      if (text !== lastText) {
        digits.textContent = text
        lastText = text
      }

      if (view.band !== lastBand) {
        root.dataset.band = view.band
        lastBand = view.band
        if (labelRef.current) {
          labelRef.current.textContent = view.band === 'FIXING' ? 'fixing strike' : ''
        }
      }

      arcRef.current?.setAttribute(
        'stroke-dasharray',
        `${view.ringFraction * RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}`,
      )
      preOpenRef.current?.setAttribute(
        'stroke-dasharray',
        `${view.preOpenFraction * RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}`,
      )
      if (barRef.current) barRef.current.style.width = `${view.ringFraction * 100}%`
    }

    // Painted once synchronously so the first frame is never a placeholder — a
    // countdown that shows `—` for 16ms on every market change flickers sixty
    // times an hour.
    paint(performance.now(), true)

    // `critical`, not `default`. The default lane shares a time budget with the
    // book flush and defers when it overruns; this lane is unbudgeted and always
    // runs to completion, which is the entire content of plan §2.4.
    return sharedScheduler().subscribe((frame) => paint(frame.timeMs), 'critical')
  }, [state, anchorAtMs, openAtMs, expiryAtMs, clock])

  if (underlay) {
    return (
      <div
        ref={rootRef}
        data-band="OPEN"
        data-stale={stale ? 'true' : undefined}
        role="timer"
        // Never a live region — at 10 Hz an announcing timer makes the page
        // unusable with a screen reader. `<MarketStatusPill>` announces the
        // state, and it changes at most eleven times a market.
        aria-live="off"
        aria-label="time remaining in this market"
        className={cx(
          'ud-countdown group pointer-events-none relative grid aspect-square place-items-center',
          'h-[70%] max-h-full max-w-[70%]',
          'data-[stale=true]:opacity-55 data-[stale=true]:saturate-50',
          className,
        )}
      >
        {/* `viewBox` rather than pixels: the ring is sized by its container, so
            it grows with the pane instead of being a 56px control that happens
            to be centred. `vector-effect` keeps the stroke at a constant screen
            width whatever that scale turns out to be. */}
        <svg
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full -rotate-90 opacity-[0.27]"
        >
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={UNDERLAY_STROKE}
            className="stroke-border-strong"
          />
          <circle
            ref={arcRef}
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={UNDERLAY_STROKE}
            strokeDasharray={`0 ${RING_CIRCUMFERENCE}`}
            strokeLinecap="round"
            className={ARC_BANDS}
          />
          <circle
            ref={preOpenRef}
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={UNDERLAY_STROKE}
            strokeDasharray={`0 ${RING_CIRCUMFERENCE}`}
            className="stroke-border-subtle"
          />
        </svg>

        {/* Inside the ring. `cqmin` so the digits scale with the pane the same
            way the ring does — a fixed type size in a container that ranges
            from 240px to 700px tall is either lost or overflowing. */}
        <span
          ref={digitsRef}
          className={cx(
            'ud-num ud-countdown-digits block text-center leading-none opacity-[0.35]',
            'text-[clamp(calc(32*var(--ud-u)),26cqmin,calc(160*var(--ud-u)))] font-semibold',
            DIGIT_BANDS,
          )}
        >
          —
        </span>
        {/* `fixing strike`, for the one state that needs a word rather than a
            number. `sr-only` because the ring already says it visually and the
            pane has no room for a caption at this scale. */}
        <span ref={labelRef} className="sr-only" />
      </div>
    )
  }

  return (
    <div
      ref={rootRef}
      data-band="OPEN"
      data-stale={stale ? 'true' : undefined}
      role="timer"
      // Never a live region. At 10 Hz an announcing timer makes the page
      // unusable with a screen reader; the state is announced by
      // `<MarketStatusPill>`, which changes at most eleven times a market.
      aria-live="off"
      aria-label="time remaining in this market"
      className={cx(
        'ud-countdown group relative flex shrink-0 items-center gap-2',
        'data-[stale=true]:opacity-55 data-[stale=true]:saturate-50',
        className,
      )}
    >
      {!bar ? (
        <svg
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          aria-hidden="true"
          className="-rotate-90 shrink-0"
        >
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            className="stroke-border-strong"
          />
          {/* Sweeps the **full 60 seconds**, not the tradable part. */}
          <circle
            ref={arcRef}
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeDasharray={`0 ${RING_CIRCUMFERENCE}`}
            className={ARC_BANDS}
          />
          {/* Drawn last, *over* the swept arc, so the ~13 seconds before `OPEN`
              stay visible as a `border-subtle` band for the whole market. That
              is the point of drawing them at all: the user should be able to see
              that the market's life began before they could trade it — 10
              seconds of strike window plus δ ≈ 3s. */}
          <circle
            ref={preOpenRef}
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeDasharray={`0 ${RING_CIRCUMFERENCE}`}
            className="stroke-border-subtle"
          />
        </svg>
      ) : null}

      <span className="flex flex-col items-start leading-none">
        <span
          ref={digitsRef}
          // `88.8` is the widest string this ever renders and `.ud-num` is
          // tabular, so the decimal can appear without moving a digit.
          className={cx('ud-num ud-countdown-digits inline-block w-[3.5ch] text-right text-display-sm', DIGIT_BANDS)}
        >
          —
        </span>
        <span ref={labelRef} className="text-micro text-text-tertiary" />
      </span>

      {bar ? (
        <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-0_5 bg-bg-active">
          <span ref={barRef} className={cx('block h-full', BAR_BANDS)} style={{ width: '0%' }} />
        </span>
      ) : null}
    </div>
  )
}
